/**
 * NoteCard: one note in the notes panel (cockatrice-ui `src/ui/NoteCard.jsx`, ported to hyperscript; PLAN.md §0).
 * variant "shared" (writer's shared note), "private" (only the writer ever sees it), "editor" (written by an editor, plum).
 * Actions show only when their handler is passed, so permissions decide what appears.
 */
import './css/note-card.css';
import { mergeProps, splitProps } from 'solid-js';
import Icon from './Icon.ts';
import { component, each, el, when, whenSome } from './solid.ts';
import type { Attributes, Child } from './solid.ts';
import { uiText } from './text.ts';

export interface NoteReply {
  /** Reply author. */
  readonly author: string;
  /** Role, e.g. "Editor". */
  readonly role?: string | undefined;
  /** Time label. */
  readonly time?: string | undefined;
  /** Reply text. */
  readonly body: string;
  /** Author is an editor (plum role). */
  readonly editor?: boolean | undefined;
}

export interface NoteCardProps extends Attributes {
  /** "shared", "private" (only the writer ever sees it) or "editor" (plum). Default "shared". */
  readonly variant?: 'shared' | 'private' | 'editor' | undefined;
  /** Author name (not shown for private notes). */
  readonly author?: string | undefined;
  /** Role label: "Writer", "Editor", "Beta reader". */
  readonly role?: string | undefined;
  /** Time: a label, or a `<time>` element the caller makes (a machine-readable date, words for assistive technology). */
  readonly time?: Child;
  /**
   * Replaces the author line (avatar, author and role; or the lock and "Only you") when the app's line says more, such as
   * who else sees the note. The time stays where it is.
   */
  readonly meta?: Child;
  /** Anchored words; leave out for a free note. */
  readonly quote?: string | undefined;
  /** Note text (or use body). */
  readonly children?: Child;
  /** Note text when not using children. */
  readonly body?: Child;
  /** Replies (never on private notes). */
  readonly replies?: readonly NoteReply[] | undefined;
  /** Resolved state. */
  readonly resolved?: boolean | undefined;
  /** The anchored text was deleted. */
  readonly detached?: boolean | undefined;
  /** Highlighted as the open note. */
  readonly active?: boolean | undefined;
  /** Makes the quote a "go to text" button. */
  readonly onSelect?: (() => void) | undefined;
  /** The go-to button's accessible name, when the app names it otherwise (a long quote cut short). */
  readonly selectLabel?: string | undefined;
  /** Shows Reply. */
  readonly onReply?: (() => void) | undefined;
  /** Shows Resolve. */
  readonly onResolve?: (() => void) | undefined;
  /** Shows Reopen on resolved notes. */
  readonly onReopen?: (() => void) | undefined;
  /** Shows Edit. */
  readonly onEdit?: (() => void) | undefined;
  /** Shows Delete. */
  readonly onDelete?: (() => void) | undefined;
  /** The caller's own actions (each named for its note, off while read-only, an inline confirm), after any built-in ones. */
  readonly actions?: Child;
  /** Extra class. */
  readonly class?: string | undefined;
}

const filled = (text: string | undefined): text is string => text !== undefined && text !== '';
/** A slot holds something to show: not left out, nothing, or an empty string. */
const given = (child: Child): boolean => child !== undefined && child !== null && child !== '';

const initials = (name = ''): string =>
  name
    .replace(/[^\p{L}\s]/gu, '')
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase() || '?';

export default function NoteCard(props: NoteCardProps): Child {
  const [local, rest] = splitProps(props, [
    'variant', 'author', 'role', 'time', 'quote', 'children', 'body', 'replies', 'resolved', 'detached', 'active',
    'onReply', 'onResolve', 'onReopen', 'onEdit', 'onDelete', 'onSelect', 'selectLabel', 'class', 'meta', 'actions',
  ]);
  const variant = (): 'shared' | 'private' | 'editor' => local.variant ?? 'shared';
  const isPrivate = (): boolean => variant() === 'private';
  const resolved = (): boolean => local.resolved === true;
  /** An action button, shown while `show` holds; its handler is read when clicked, as the handoff binds it. */
  const action = (show: () => boolean, label: () => string, run: () => (() => void) | undefined, danger = false): Child =>
    when(show, () =>
      el(
        'button',
        {
          type: 'button',
          class: danger ? 'is-danger' : undefined,
          onClick: () => {
            run()?.();
          },
        },
        label,
      ),
    );

  // `{...rest}` comes last in the handoff's JSX, so what the caller passes wins over the card's own attributes.
  return el(
    'article',
    mergeProps(
      {
        class: () =>
          `ui-note-card ui-note-card--${variant()}${resolved() ? ' is-resolved' : ''}${local.active === true ? ' is-active' : ''}${filled(local.class) ? ` ${local.class}` : ''}`,
        'aria-label': () =>
          isPrivate() ? uiText()('ui.note-card.private') : filled(local.author) ? uiText()('ui.note-card.by', { author: local.author }) : uiText()('ui.note-card.by-you'),
        'aria-current': () => (local.active === true ? 'true' : undefined),
      },
      rest,
    ),
    el(
      'header',
      { class: 'ui-note-card__meta' },
      when(
        () => given(local.meta),
        () => local.meta,
        () =>
          when(
            isPrivate,
            () => el('span', { class: 'ui-note-card__private' }, component(Icon, { name: 'lock', size: 'sm' }), () => uiText()('ui.note-card.only-you')),
            () => [
              el('span', { class: 'ui-note-card__avatar', 'aria-hidden': 'true' }, () => initials(local.author)),
              el('span', { class: 'ui-note-card__author' }, () => local.author),
              when(
                () => filled(local.role),
                () => el('span', { class: 'ui-note-card__role' }, () => local.role),
              ),
            ],
          ),
      ),
      when(
        () => given(local.time),
        () => el('span', { class: 'ui-note-card__time' }, () => local.time),
      ),
    ),

    when(
      () => local.detached === true,
      () => el('p', { class: 'ui-note-card__detached' }, component(Icon, { name: 'info', size: 'sm' }), () => uiText()('ui.note-card.detached')),
    ),
    // `whenSome` shows the quote while it is a non-empty string, as `filled` does, and hands it over narrowed.
    whenSome(
      () => local.quote,
      (quote) =>
        el(
          'blockquote',
          { class: 'ui-note-card__quote' },
          when(
            () => local.onSelect !== undefined,
            () =>
              el(
                'button',
                {
                  type: 'button',
                  class: 'ui-note-card__quote-link',
                  onClick: () => {
                    local.onSelect?.();
                  },
                  'aria-label': () => (filled(local.selectLabel) ? local.selectLabel : uiText()('ui.note-card.go-to', { quote: quote() })),
                },
                '“',
                () => local.quote,
                '”',
              ),
            () => ['“', () => local.quote, '”'],
          ),
        ),
    ),

    el('div', { class: 'ui-note-card__body' }, () => local.children ?? local.body),

    whenSome(
      () => ((local.replies?.length ?? 0) > 0 ? local.replies : undefined),
      (replies) =>
        el(
          'ol',
          { class: 'ui-note-card__replies', 'aria-label': () => uiText()('ui.note-card.replies') },
          each(
            replies,
            (r) =>
              el(
                'li',
                { class: `ui-note-card__reply${r.editor === true ? ' is-editor' : ''}` },
                el(
                  'div',
                  { class: 'ui-note-card__reply-meta' },
                  el('span', { class: 'ui-note-card__author' }, r.author),
                  when(
                    () => filled(r.role),
                    () => el('span', { class: 'ui-note-card__role' }, r.role),
                  ),
                  when(
                    () => filled(r.time),
                    () => el('span', { class: 'ui-note-card__time' }, r.time),
                  ),
                ),
                el('p', {}, r.body),
              ),
          ),
        ),
    ),

    when(resolved, () => el('p', { class: 'ui-note-card__resolved' }, component(Icon, { name: 'check', size: 'sm' }), () => uiText()('ui.note-card.resolved'))),

    when(
      () => local.onReply !== undefined || local.onResolve !== undefined || local.onReopen !== undefined || local.onEdit !== undefined || local.onDelete !== undefined || given(local.actions),
      () =>
        el(
          'footer',
          { class: 'ui-note-card__actions' },
          // A private note is never offered a reply: only the writer ever sees it.
          action(() => local.onReply !== undefined && !isPrivate() && !resolved(), () => uiText()('ui.note-card.reply'), () => local.onReply),
          action(() => local.onResolve !== undefined && !resolved(), () => uiText()('ui.note-card.resolve'), () => local.onResolve),
          action(() => local.onReopen !== undefined && resolved(), () => uiText()('ui.note-card.reopen'), () => local.onReopen),
          action(() => local.onEdit !== undefined, () => uiText()('ui.note-card.edit'), () => local.onEdit),
          action(() => local.onDelete !== undefined, () => uiText()('ui.note-card.delete'), () => local.onDelete, true),
          () => local.actions,
        ),
    ),
  );
}
